import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

export const HOW_HEARD_OPTIONS = [
  'Instagram',
  'Facebook',
  'YouTube',
  'Word of mouth',
  'Previous visit',
  'Google',
  'Sky TV',
  'Mobile apps',
  'Newsletter',
  'Flyer',
  'Digital advertising screen',
  'Other',
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  width: min(640px, 100%);
  padding: 0 1.25rem;
  animation: ${fadeIn} 0.35s ease;
  text-align: center;
`;

const Status = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.55);
  font-weight: 600;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.45rem, 4vw, 1.9rem);
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.98rem;
  color: rgba(16, 29, 64, 0.65);
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 1rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Option = styled.button`
  border: 1.5px solid ${props => (props.$active ? props.theme.colors.primary : 'rgba(16, 29, 64, 0.14)')};
  background: ${props => (props.$active ? props.theme.colors.primary : 'white')};
  color: ${props => (props.$active ? 'white' : props.theme.colors.primary)};
  border-radius: 10px;
  padding: 0.85rem 0.9rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const OtherInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: 1.5px solid rgba(16, 29, 64, 0.18);
  font-size: 0.95rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  align-items: center;
`;

const ContinueBtn = styled.button`
  width: 100%;
  max-width: 320px;
  border: none;
  border-radius: 10px;
  padding: 0.95rem 1.25rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-size: 1rem;
  font-weight: 700;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const SkipBtn = styled.button`
  border: none;
  background: transparent;
  color: rgba(16, 29, 64, 0.55);
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.35rem;
`;

const ProgressNote = styled.p`
  margin: 1.25rem 0 0;
  font-size: 0.85rem;
  color: rgba(16, 29, 64, 0.5);
`;

/**
 * Shown while a booking is being confirmed.
 * onComplete({ source, otherText }) or onComplete(null) for skip.
 */
const HowHeardSurvey = ({ confirming = true, onComplete }) => {
  const [source, setSource] = useState('');
  const [otherText, setOtherText] = useState('');

  const canContinue =
    !!source && (source !== 'Other' || otherText.trim().length > 0);

  return (
    <Wrap>
      <Status>{confirming ? 'Confirming your booking…' : 'Almost done'}</Status>
      <Title>How did you hear about us?</Title>
      <Subtitle>This helps us understand what brings guests to Belvoir Castle.</Subtitle>

      <Grid>
        {HOW_HEARD_OPTIONS.map((opt) => (
          <Option
            key={opt}
            type="button"
            $active={source === opt}
            onClick={() => setSource(opt)}
          >
            {opt}
          </Option>
        ))}
      </Grid>

      {source === 'Other' && (
        <OtherInput
          type="text"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Please tell us how you heard about us"
          autoFocus
          maxLength={200}
        />
      )}

      <Actions>
        <ContinueBtn
          type="button"
          disabled={!canContinue}
          onClick={() =>
            onComplete({
              source,
              otherText: source === 'Other' ? otherText.trim() : '',
            })
          }
        >
          Continue
        </ContinueBtn>
        <SkipBtn type="button" onClick={() => onComplete(null)}>
          Skip for now
        </SkipBtn>
      </Actions>

      {confirming && (
        <ProgressNote>We’re securing your booking in the background.</ProgressNote>
      )}
    </Wrap>
  );
};

export default HowHeardSurvey;
